import {
  Component, Input, Output, EventEmitter,
  ContentChildren, QueryList, OnInit, OnDestroy
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { TableCellDirective } from './table-cell.directive';
import { TableColumn } from './table-column.interface';
import { faChevronLeft, faChevronRight,faArrowUp, faArrowDown, faArrowsUpDown } from '@fortawesome/pro-solid-svg-icons';

export interface DateRangeFilter {
  column: string;
  from?: string;
  to?: string;
}

export interface TableSearchRequest {
  page: number;
  pageSize: number;
  orderBy: string;
  orderDirection: 'asc' | 'desc' | '';
  filterColumns: string[];
  filterStrings: string[];
  dateRangeFilters: DateRangeFilter[];
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent {

@ContentChildren(TableCellDirective) cellDefs!: QueryList<TableCellDirective>;

  private _columns: TableColumn[] = [];

  @Input() set columns(value: TableColumn[]) {
    this._columns = value;
    value
      .filter(c => c.filterType === 'daterange')
      .forEach(c => {
        if (!this.dateRangeValues[c.key]) {
          this.dateRangeValues[c.key] = { from: null, to: null };
        }
      });
  }

  get columns(): TableColumn[] {
    return this._columns;
  }
  @Input() data: any[] = [];
  @Input() totalCount: number = 0;
  @Input() pageSize: number = 20;
  @Input() loading: boolean = false;
  @Input() clickableRows: boolean = false;

  @Input() initialFilterValues: { [key: string]: string } = {};

  @Input() compact: boolean = false;
  @Input() pageSizeOptions: number[] = [10, 20, 50, 100];

  @Output() searchChange = new EventEmitter<TableSearchRequest>();
  @Output() rowClick = new EventEmitter<any>();

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  currentPage: number = 1;
  filterValues: { [key: string]: string } = {};
  dateRangeValues: { [key: string]: { from: string | null, to: string | null } } = {};

  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faArrowUp = faArrowUp;
  faArrowDown = faArrowDown;
  faArrowsUpDown = faArrowsUpDown;
  

  get hasFilters(): boolean {
    return this.columns.some(c => c.filterable);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get showingFrom(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  get pages(): (number | '...')[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const cur = this.currentPage;
    const result: (number | '...')[] = [1];

    if (cur > 3) result.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
      result.push(i);
    }
    if (cur < total - 2) result.push('...');
    result.push(total);

    return result;
  }

  ngOnInit() {

    // initialise select filters to empty string (All)
    this.columns
      .filter(c => c.filterable && c.filterType === 'select')
      .forEach(c => {
        if (this.filterValues[c.key] === undefined) {
          this.filterValues[c.key] = '';
        }
      });

     // apply initial filter values on top (these take priority)
    this.filterValues = { ...this.filterValues, ...this.initialFilterValues };

    this.columns
    .filter(c => c.filterType === 'daterange')
    .forEach(c => this.dateRangeValues[c.key] = { from: null, to: null });

    this.filterSubject.pipe(
      debounceTime(350),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.emit();
    });

    // initial load
    this.emit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sort(col: TableColumn) {
    if (!col.sortable) return;
    if (this.sortColumn === col.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = col.key;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.emit();
  }

  onFilterChange() {
    this.filterSubject.next();
  }

  goToPage(page: number | '...') {
    if (page === '...') return;
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.emit();
  }

  getTemplate(key: string) {
    return this.cellDefs?.find(d => d.column === key)?.template;
  }

  // supports dot-notation keys e.g. 'companyGroup.name'
  getCellValue(row: any, key: string): any {
    return key.split('.').reduce((obj, k) => obj?.[k], row);
  }

  onDateRangeChange(key: string, field: 'from' | 'to', value: string | null) {
    if (!this.dateRangeValues[key]) {
      this.dateRangeValues[key] = { from: null, to: null };
    }
    this.dateRangeValues[key][field] = value;
    
    this.filterSubject.next();
  }

  private emit() {
      const filterableCols = this.columns.filter(c => c.filterable && c.filterType !== 'daterange');
      const dateRangeCols = this.columns.filter(c => c.filterable && c.filterType === 'daterange');

      const activeFilterCols: string[] = [];
      const activeFilterStrings: string[] = [];

      filterableCols.forEach(c => {
        const val = this.filterValues[c.key] ?? '';
        if (val !== '') {
          activeFilterCols.push(c.filterKey ?? c.key);
          activeFilterStrings.push(val);
        }
      });

    this.searchChange.emit({
      page: this.currentPage,
      pageSize: this.pageSize,
      orderBy: this.sortColumn,
      orderDirection: this.sortDirection,
      filterColumns: filterableCols.map(c => c.filterKey ?? c.key),
      filterStrings: filterableCols.map(c => this.filterValues[c.key] ?? ''),
      dateRangeFilters: dateRangeCols
        .filter(c => this.dateRangeValues[c.key]?.from || this.dateRangeValues[c.key]?.to)
        .map(c => ({
          column: c.filterKey ?? c.key,
          from: this.dateRangeValues[c.key].from ?? undefined,
          to: this.dateRangeValues[c.key].to ?? undefined
        }))
    });
  }

  onPageSizeChange() {
    this.currentPage = 1;
    this.emit();
  }
}
